import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap, tap, throttleTime, withLatestFrom } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { ResellersService } from '@services/resellers/resellers.service'
import { AuthActions, ResellersActions } from '@actions/index'
import { HttpApiService } from '@services/http-api.service';
import { ResellerModel, UserModel } from '@models/index';
import { RouterFacadeService } from '@services/router-facade.service';
import { ResellersFacadeService } from '@services/resellers/resellers-facade.service';
import { emptyResellerModel, emptyBusinessModel, emptyLocationModel } from '@utilities/data-utils';
import { BusinessFacadeService } from '@services/business/business-facade.service';
import { LocationsFacadeService } from '@services/locations/locations-facade.service';
import { GMBFailures } from '@enums/GMBFailures.enum';
import * as fromApp from '@reducers/index'
import { Store } from '@ngrx/store';

@Injectable()
export class ResellersEffects {

    searchResllers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ResellersActions.searchResellers),
            switchMap(({ request }) =>
                this.httpApiService
                    .getRequest(request)
                    .pipe(
                        map(data => data as { data: ResellerModel[], total: number }),
                        map(data => ResellersActions.searchResellersSuccess({ ...data })),
                        catchError(error => of(ResellersActions.resllersError({ error: error.message })))
                    )
            )
        )
    );

    getResellerById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ResellersActions.getResellerById),
            switchMap(({ request }) =>
                this.httpApiService
                    .getRequest(request)
                    .pipe(
                        map(data => data as ResellerModel),
                        map(reseller => ResellersActions.getResellerByIdSuccess({ reseller })),
                        catchError(error => of(ResellersActions.resllersError({ error: error.message })))
                    )
            )
        )
    );

    createReseller$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ResellersActions.createReseller),
            throttleTime(4000),
            switchMap(({ request }) =>
                this.httpApiService
                    .postRequest(request)
                    .pipe(
                        map(data => data as ResellerModel),
                        tap(data => this.launchToastMssg('success', 'Create Reseller', `The reseller ${data.name} has been created`)),
                        map(reseller => ResellersActions.createResellerSuccess({ reseller })),
                        tap(() => this.routerFacadeService.navigateTo$('reseller/business')),
                        catchError(error => of(ResellersActions.resllersError({ error: error.message })))
                    )
            )
        )
    );

    searchForResellerAdminLocationAutocomplete$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ResellersActions.selectResellerProfile),
            withLatestFrom(
                this.store.select(fromApp.getLoggedUser),
            ),
            map(([{ reseller }, user]) => [reseller, user]),
            switchMap(([reseller, user]: [ResellerModel, UserModel]) => {
                if (user.role.name == "SITE_ADMIN") {
                    return of(
                        AuthActions.searchLocationAutocompleteOptions({ resellerId: reseller.id })
                    );
                } else {
                    return EMPTY;
                }
            })
        ),
    )

    deleteReseller$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ResellersActions.deleteReseller),
            map(action => action.resellerId),
            switchMap(resellerId =>
                this.resellersService
                    .deleteReseller(resellerId)
                    .pipe(
                        tap(data => this.launchToastMssg('success', 'Delete Reseller', 'The reseller has been deleted')),
                        map(resellerId => ResellersActions.deleteResellerSuccess({ resellerId })),
                        tap(() => {
                            this.routerFacadeService.navigateTo$('admin/reseller');
                            this.resellersFacadeServices.selectResellerPorfile(emptyResellerModel());
                            this.businessFacadeService.selectBusinessProfile(emptyBusinessModel());
                            this.locationFacadeService.setCurrentLocation(emptyLocationModel())
                        }),
                        catchError(error => of(ResellersActions.resllersError({ error: error.message })))
                    )
            )
        )
    );

    archiveReseller$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ResellersActions.archiveReseller),
            map(action => action.resellerId),
            switchMap(resellerId =>
                this.resellersService
                    .archiveReseller(resellerId)
                    .pipe(
                        tap(_data => this.launchToastMssg('success', 'Archive Reseller', 'The reseller has been archived')),
                        map(resellerId => ResellersActions.archiveResellerSuccess({ resellerId })),
                        catchError(error => of(ResellersActions.resllersError({ error: error.message })))
                    )
            )
        )
    );

    editReseller$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ResellersActions.editReseller),
            map(action => action.reseller),
            switchMap(reseller =>
                this.resellersService
                    .editReseller(reseller)
                    .pipe(
                        tap(reseller => this.launchToastMssg('success', 'Edit Reseller', `The reseller ${reseller.name} has been edited`)),
                        map(reseller => ResellersActions.editResellerSuccess({ reseller })),
                        tap(() => {
                            this.routerFacadeService.navigateTo$('reseller/reseller-profile');
                            // refresh the page so app.component.ts will reload the reseller in case the logo has been updated
                            setTimeout(() => {
                                this.window.location = '/reseller/reseller-profile';
                            }, 2500);
                        }),
                        catchError(error => of(ResellersActions.resllersError({ error: error.message })))
                    )
            )
        )
    );

    editResellerAvailableLicesnes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ResellersActions.editResellerAvailableLicenses),
            map(action => action.reseller),
            switchMap(reseller =>
                this.resellersService
                    .editResellerAvailableLicenses(reseller)
                    .pipe(
                        tap(reseller => this.launchToastMssg('success', 'Edit Reseller', `The reseller ${reseller.name} has been edited`)),
                        map(reseller => ResellersActions.editResellerSuccess({ reseller })),
                        // tap(() => {
                        //     this.routerFacadeService.navigateTo$('reseller/reseller-profile');
                        //     // refresh the page so app.component.ts will reload the reseller in case the logo has been updated
                        //     setTimeout(() => {
                        //         this.window.location = '/reseller/reseller-profile';
                        //     }, 2500);
                        // }),
                        //catchError(error => of(ResellersActions.resllersError({ error: error.message })))
                        catchError(error => {
                            if (error.error.message == GMBFailures.AuthFailure) {
                                return of(ResellersActions.resllersError({ error: error.message }))
                            }
                            this.launchToastMssg('error', error.error.message, 'Error');
                            return of(ResellersActions.resllersError({ error: error.message }))
                        })
                    )
            )
        )
    );

    searchResellersByQuery$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ResellersActions.searchResellersByQuery),
            switchMap(({ request }) =>
                this.httpApiService
                    .getRequest(request)
                    .pipe(
                        map(data => data as  { data: ResellerModel[], total: number }),
                        map(data => ResellersActions.searchResellersSuccess({ ...data })),
                        catchError(error => of(ResellersActions.resllersError({ error: error.message })))
                    )
            )
        )
    );

    searchResellerByBusiness$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ResellersActions.searchResellerByBusiness),
            switchMap(({ request }) =>
                this.httpApiService
                    .getRequest(request)
                    .pipe(
                        map(data => data as ResellerModel),
                        map(reseller => ResellersActions.searchResellerByBusinessSuccess({ reseller })),
                        catchError(error => of(ResellersActions.resllersError({ error: error.message })))
                    )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private resellersService: ResellersService,
        private routerFacadeService: RouterFacadeService,
        private resellersFacadeServices: ResellersFacadeService,
        private businessFacadeService: BusinessFacadeService,
        private locationFacadeService: LocationsFacadeService,
        private messageToast: MessageService,
        private httpApiService: HttpApiService,
        private window: Window,
        private store: Store<any>,
    ) { }

    launchToastMssg(severity: string, summary: string, detail: string) {
        this.messageToast.add({ severity: severity, summary: summary, detail: detail, life:5000 });
    }
}
